import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import stripeApiService from '../../services/stripe-subscription.service';
import { delay } from '../../utils/helpers';
import {
  IconCalendar,
  IconClock,
  IconX,
  IconInfoCircle,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import PropTypes from 'prop-types';
import './ChangeFrequency.css';

function ChangeFrequency({
  isOpen,
  onClose,
  organization,
  currentInterval,
  stripeSubscriptionId,
  currentPlanId,
  onFrequencyChanged,
  discount,
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [alternativePlan, setAlternativePlan] = useState(null);

  // Fetch available plans when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailablePlans();
    }
  }, [isOpen, currentPlanId]);

  const fetchAvailablePlans = async () => {
    setIsLoading(true);
    try {
      const response = await stripeApiService.getProductList();

      // Find the product that contains the current plan ID
      const currentProductData = response.products.find((productData) =>
        productData.prices.some((price) => price.id === currentPlanId),
      );

      if (currentProductData) {
        // Use the prices from the found product
        setAvailablePlans(currentProductData.prices);

        // Find the alternative plan (if current is monthly, find yearly and vice versa)
        const targetInterval = currentInterval === 'month' ? 'year' : 'month';
        const alternativePlanOption = currentProductData.prices.find(
          (price) => price.recurring.interval === targetInterval,
        );

        if (alternativePlanOption) {
          setAlternativePlan(alternativePlanOption);
          setSelectedPlanId(alternativePlanOption.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load available plans. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFrequency = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a plan to continue.');
      return;
    }

    setIsProcessing(true);
    try {
      // Call API to change subscription plan
      await stripeApiService.changeSubscriptionPlan(
        stripeSubscriptionId,
        selectedPlanId,
        organization,
      );

      await delay(3000);
      toast.success(
        `Subscription frequency successfully changed to ${alternativePlan?.recurring.interval}ly`,
      );
      onFrequencyChanged();
    } catch (error) {
      console.error('Error changing subscription frequency:', error);
      toast.error('Failed to change subscription frequency. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatIntervalLabel = (interval) => {
    if (interval === 'year') {
      return 'Annually';
    }
    return 'Monthly';
  };

  const formatAmount = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  // Calculate discounted price if discount is active
  const calculateDiscountedPrice = (basePrice) => {
    if (!discount || !discount.valid) return basePrice;

    const { discount_type, discount_value } = discount;
    
    if (discount_type === 'percent') {
      return basePrice * (1 - discount_value / 100);
    } else {
      // Amount-based discount (discount_value is in cents)
      return basePrice - discount_value;
    }
  };

  const hasActiveDiscount = discount && discount.valid;

  const getIntervalDescription = (interval) => {
    if (interval === 'month') {
      return 'Billed monthly. Offers more flexibility, but costs more over time than the annual plan.';
    } else {
      return 'Billed annually. Includes a 20% savings compared to monthly billing.';
    }
  };

  // Calculate savings percentage between monthly and yearly plans
  const calculateSavings = () => {
    const monthlyPlan = availablePlans.find(
      (plan) => plan.recurring.interval === 'month',
    );
    const yearlyPlan = availablePlans.find(
      (plan) => plan.recurring.interval === 'year',
    );

    if (monthlyPlan && yearlyPlan) {
      const monthlyCostForYear = monthlyPlan.unit_amount * 12;
      const yearlyCost = yearlyPlan.unit_amount;
      const savings =
        ((monthlyCostForYear - yearlyCost) / monthlyCostForYear) * 100;
      return Math.round(savings);
    }

    return 0;
  };

  if (isLoading) {
    return (
      <Modal
        show={isOpen}
        onHide={onClose}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Header className="change-frequency-modal-header">
          <div>
            <Modal.Title as="h3">
              <IconCalendar size={24} className="me-2" />
              Change Billing Frequency
            </Modal.Title>
            <p className="modal-subtitle">
              Switch between monthly and annual billing
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className="change-frequency-modal-body">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading available plans...</p>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Header className="change-frequency-modal-header">
        <div>
          <Modal.Title as="h3">
            <IconCalendar size={24} className="me-2" />
            Change Billing Frequency
          </Modal.Title>
          <p className="modal-subtitle">
            Switch between monthly and annual billing
          </p>
        </div>
        <button
          className="modal-close-btn"
          onClick={onClose}
          disabled={isProcessing}
        >
          <IconX size={20} />
        </button>
      </Modal.Header>
      <Modal.Body className="change-frequency-modal-body">
        {alternativePlan ? (
          <>
            <p className="frequency-intro-text">
              You're about to switch your billing from{' '}
              <strong>{formatIntervalLabel(currentInterval)}</strong> to{' '}
              <strong>
                {formatIntervalLabel(alternativePlan.recurring.interval)}
              </strong>
              .
            </p>

            <div className="plans-container">
              {/* Current Plan Card */}
              <div className="plan-card current-plan-card">
                <div className="plan-card-header">
                  <div className="plan-card-left">
                    <IconClock size={20} className="plan-icon" />
                    <div className="plan-info">
                      <h4 className="plan-title">
                        Current Plan
                        <span className="badge-interval">
                          {formatIntervalLabel(currentInterval)}
                        </span>
                      </h4>
                    </div>
                  </div>
                  <div className="plan-price">
                    {(() => {
                      const currentPlan = availablePlans.find(
                        (p) => p.recurring.interval === currentInterval,
                      );
                      const basePrice = currentPlan?.unit_amount || 0;
                      const discountedPrice = calculateDiscountedPrice(basePrice);
                      
                      return (
                        <>
                          {hasActiveDiscount && (
                            <span style={{ textDecoration: 'line-through', marginRight: '6px', opacity: 0.6, fontSize: '0.9em' }}>
                              {formatAmount(basePrice)}
                            </span>
                          )}
                          {formatAmount(discountedPrice)}
                        </>
                      );
                    })()}
                    /{currentInterval === 'year' ? 'year' : 'month'}
                  </div>
                </div>
                <p className="plan-description">
                  {getIntervalDescription(currentInterval)}
                </p>
              </div>

              {/* New Plan Card */}
              <div className="plan-card new-plan-card">
                <div className="plan-card-header">
                  <div className="plan-card-left">
                    <IconCalendar size={20} className="plan-icon" />
                    <div className="plan-info">
                      <h4 className="plan-title">
                        New Plan
                        <span className="badge-interval">
                          {formatIntervalLabel(
                            alternativePlan.recurring.interval,
                          )}
                        </span>
                      </h4>
                    </div>
                  </div>
                  <div className="plan-price">
                    {(() => {
                      const basePrice = alternativePlan.unit_amount;
                      const discountedPrice = calculateDiscountedPrice(basePrice);
                      
                      return (
                        <>
                          {hasActiveDiscount && (
                            <span style={{ textDecoration: 'line-through', marginRight: '6px', opacity: 0.6, fontSize: '0.9em' }}>
                              {formatAmount(basePrice)}
                            </span>
                          )}
                          {formatAmount(discountedPrice)}
                        </>
                      );
                    })()}/
                    {alternativePlan.recurring.interval === 'year'
                      ? 'year'
                      : 'month'}
                  </div>
                </div>
                <p className="plan-description">
                  {getIntervalDescription(alternativePlan.recurring.interval)}
                </p>
              </div>
            </div>

            <div className="info-notice">
              <IconInfoCircle size={18} />
              <p>
                Note: This change will take effect immediately, and your billing
                cycle will be updated accordingly.
              </p>
            </div>
          </>
        ) : (
          <div className="empty-state-warning">
            <IconAlertTriangle size={20} />
            <p>
              No alternative billing frequency is available for the current
              plan. Please contact support for assistance.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="change-frequency-modal-footer">
        <button
          className="btn-cancel"
          disabled={isProcessing}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className="btn-confirm"
          onClick={handleChangeFrequency}
          disabled={isProcessing || !alternativePlan}
        >
          {isProcessing ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <IconCheck size={16} />
              Confirm Change
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

ChangeFrequency.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  currentInterval: PropTypes.string,
  stripeSubscriptionId: PropTypes.string,
  currentPlanId: PropTypes.string,
  onFrequencyChanged: PropTypes.func,
  discount: PropTypes.object,
};

export default ChangeFrequency;
